import { ConsentWorkflow, IEConsentForm, LookupYesNoType, Patient } from '@/api/dtos';
import useConsentVersionLookup, { PICF_VERSION_OTHER_ID } from '@/hooks/lookups/useConsentVersionLookup';
import useInvestigatorLookup, { INVESTIGATOR_LOOKUP_OTHER } from '@/hooks/lookups/useInvestigatorLookup';
import { ConditionMode, useScopedField } from '@ngt/forms-core';
import {
    CrfCondition,
    DatePicker,
    FormLookupsContext,
    GroupedField,
    GroupedFields,
    ICrfConditionParameters,
    InstitutionContext,
    PatientContext,
    Select,
    Text,
    ToggleButtonGroup,
} from '@ngt/opms';
import { LinearProgress } from '@mui/material';
import { FC, PropsWithChildren, useCallback, useContext, useMemo } from 'react';
import SelectWithTooltip from '@/components/forms/inputs/SelectWithTooltip';

const patientSigned = ({ formState }: ICrfConditionParameters<IEConsentForm>) =>
    formState?.value?.patientSignedConsent === LookupYesNoType.Yes;
const investigatorSigned = ({ formState }: ICrfConditionParameters<IEConsentForm>) =>
    formState?.value?.investigatorSignedConsent === LookupYesNoType.Yes;
const picfVersionOther = ({ formState }: ICrfConditionParameters<IEConsentForm>) =>
    formState?.value?.picfId === PICF_VERSION_OTHER_ID;
const investigatorOther = ({ formState }: ICrfConditionParameters<IEConsentForm>) =>
    formState?.value?.assignedInvestigatorUsername === INVESTIGATOR_LOOKUP_OTHER;

interface PicfVersionFieldProps {
    picfTypeName: string;
}

const PicfVersionField: FC<PicfVersionFieldProps> = ({ picfTypeName }) => {
    const { data: patient } = useContext(PatientContext);
    const { state: { value: workflow } } = useScopedField<ConsentWorkflow>('workflow', { value: true }, false, false);
    const { state: { value: picfId } } = useScopedField<number>('picfId', { value: true }, false, false);
    const { context: { setValue: setPicfVersion } } = useScopedField<string>('picfVersion', { value: true }, false, false);
    const { data: picfLookup, loading } = useConsentVersionLookup(
        'picfId',
        (patient as Patient)?.institutionCode,
        picfTypeName,
        false
    );
    const picfLookups = useMemo(() => [picfLookup], [picfLookup]);
    const selectedVersion = picfLookup?.items?.find(item => item.id === picfId)?.value;

    const onChange = useCallback((event: any) => {
        const version = picfLookup?.items?.find(item => item.id === event.target.value)?.value;
        if (version) {
            setPicfVersion(version, true, true, true, false);
        }
    }, [picfLookup, setPicfVersion]);

    return (
        <FormLookupsContext.Provider value={picfLookups}>
            {loading
                ? <LinearProgress color="primary" />
                : (
                    <GroupedFields>
                        <GroupedField
                            propertyPath="picfId"
                            component={SelectWithTooltip}
                            onChange={onChange}
                            disabled={workflow === ConsentWorkflow.EConsent}
                            FieldGridProps={{ size: { xl: 3, lg: 3, md: 5, sm: 12, xs: 12 } }}
                            tooltipProps={{ arrow: true, placement: 'right', title: selectedVersion }}
                        />
                        <CrfCondition
                            mode={ConditionMode.Show}
                            condition={picfVersionOther}
                            subscription={{ value: true }}
                        >
                            <GroupedField propertyPath="picfVersionSpecify" component={Text} />
                        </CrfCondition>
                    </GroupedFields>
                )}
        </FormLookupsContext.Provider>
    );
};

export interface ConsentStandardFieldsProps extends PropsWithChildren {
    picfTypeName: string;
}

const ConsentStandardFields: FC<ConsentStandardFieldsProps> = ({ picfTypeName, children }) => {
    const { data: institution } = useContext(InstitutionContext);
    const { data: investigatorLookup, loading: investigatorsLoading } = useInvestigatorLookup(
        'assignedInvestigatorUsername',
        institution?.code
    );
    const investigators = useMemo(() => [investigatorLookup], [investigatorLookup]);
    const { state: { value: workflow } } = useScopedField<ConsentWorkflow>('workflow', { value: true }, false, false);
    const disabled = workflow !== ConsentWorkflow.Standard;

    return (
        <>
            <GroupedField propertyPath="patientSignedConsent" component={ToggleButtonGroup} disabled={disabled} />
            <CrfCondition mode={ConditionMode.Enable} condition={patientSigned} subscription={{ value: true }}>
                <PicfVersionField picfTypeName={picfTypeName} />
                <GroupedField propertyPath="datePatientSignedConsent" component={DatePicker} disabled={disabled} />
                {children}
                <GroupedField propertyPath="investigatorSignedConsent" component={ToggleButtonGroup} disabled={disabled} />
                <CrfCondition mode={ConditionMode.Enable} condition={investigatorSigned} subscription={{ value: true }}>
                    <GroupedField propertyPath="dateInvestigatorSignedConsent" component={DatePicker} disabled={disabled} />
                    <FormLookupsContext.Provider value={investigators}>
                        {investigatorsLoading
                            ? <LinearProgress color="primary" />
                            : (
                                <GroupedFields>
                                    <GroupedField
                                        propertyPath="assignedInvestigatorUsername"
                                        component={Select}
                                        disabled={disabled}
                                    />
                                    <CrfCondition
                                        mode={ConditionMode.Show}
                                        condition={investigatorOther}
                                        subscription={{ value: true }}
                                    >
                                        <GroupedField
                                            propertyPath="assignedInvestigatorSpecify"
                                            component={Text}
                                            disabled={disabled}
                                        />
                                    </CrfCondition>
                                </GroupedFields>
                            )}
                    </FormLookupsContext.Provider>
                </CrfCondition>
            </CrfCondition>
        </>
    );
};

export default ConsentStandardFields;
